import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from 'react-apollo';
import { useKeycloak } from 'react-keycloak';
import { useTranslation } from 'react-i18next';

import '../i18n';
import '../styles/global.css';
import '../../node_modules/bootstrap-utilities/bootstrap-utilities.css';

import { useDispatch, useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import Seo from './Seo';
import Viewer from './Viewer/Viewer';
import { AppContainer } from './Common/styled/AppContainer';
import GlobalStyle from '../themes/GlobalStyle';
import GlobalThemeStyles from '../themes/GlobalThemeStyles';
import { ToastContainer } from './Common/Toast/ToastContainer';
import Loading from './Common/Loading';
import { isEmpty } from '../utils/ObjectUtils';
import { StudyUploadWidget } from './Widget/Upload/StudyUploadWidget';

import { GET_PATIENT_HOSPITALS, GET_USER_PATIENTS } from '../graphql/queries';
import { getSessionStorageJson } from '../utils/ReadStorageUtils';
import { getCurrentPatientUserHospital } from '../utils/HospitalUtils';
import { AlertContainerUI, AlertUI } from '../componentsUI/Alert';
import { EmptyPageModern } from './Common/styled/EmptyPage';
import { StudyDownloadWidget } from './Widget/Download/StudyDownloadWidget';
import Version from "./Version";

export default ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [hospitalData, setHospitalData] = useState(null);
  const [patientsData, setPatientsData] = useState(null);

  const [getPatients, { loading: loadingPatients, data: queryPatients }] = useLazyQuery(GET_USER_PATIENTS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      sessionStorage.setItem('patientsData', JSON.stringify(d));
    },
  });

  const [getHospitals, { data: queryHospitals }] = useLazyQuery(GET_PATIENT_HOSPITALS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      sessionStorage.setItem('hospitalData', JSON.stringify(d));
    },
  });

  const reduxHospital = useSelector((state) => state.hospital);
  const reduxUser = useSelector((state) => state.userInterface.user);

  const sessionHospitals = getSessionStorageJson('hospitalData');
  const sessionPatients = getSessionStorageJson('patientsData');

  const bootstrapHospitalData = () => {
    if (!isEmpty(hospitalData)) return;
    if (queryHospitals) return setHospitalData(queryHospitals);
    if (sessionHospitals) setHospitalData(sessionHospitals);
  };

  const bootstrapPatientsData = () => {
    if (!isEmpty(patientsData)) return;
    if (queryPatients) return setPatientsData(queryPatients);
    if (sessionPatients) setPatientsData(sessionPatients);
  };

  useEffect(() => {
    bootstrapHospitalData();
  }, [queryHospitals, sessionHospitals]);

  useEffect(() => {
    bootstrapPatientsData();
  }, [queryPatients, sessionPatients]);

  useEffect(() => {
    if (!hospitalData) getHospitals();
    if (!patientsData) getPatients();
  }, []);

  const themeContext = useContext(ThemeContext);
  const [keycloak] = useKeycloak();

  if (!keycloak.tokenParsed.roles.includes('https://api.cloud.alma-medical.com/patient')) {
    if (keycloak.tokenParsed.roles.includes('https://api.cloud.alma-medical.com/doctor')) {
      window.location.href = process.env.GATSBY_APP_CLOUD_URL;
    }

    return <div>You don&apos;t have the permission to access the platform</div>;
  }

  if (!hospitalData || !patientsData) return 'Loading Data...';

  if (loadingPatients && isEmpty(patientsData)) return <Loading />;

  const userHospital = getCurrentPatientUserHospital(patientsData, hospitalData, reduxUser);

  if (!userHospital) {
    return (
      <AppContainer>
        <EmptyPageModern>
          <AlertContainerUI>
            <AlertUI severity="warning" title={t('access.forbidden')}>{t('you.do.not.have.access.to.any.existing.hospital')}</AlertUI>
          </AlertContainerUI>
        </EmptyPageModern>
      </AppContainer>
    );
  }

  if (userHospital && !reduxHospital) {
    dispatch({ type: 'CHANGE_HOSPITAL', current: userHospital });
  }

  if (reduxHospital && reduxHospital.mainColor) {
    themeContext.setPrimaryColor(reduxHospital.mainColor);
  }

  return (
    <AppContainer>
      <Seo />
      <GlobalStyle />
      <GlobalThemeStyles />
      {children}
      <ToastContainer />
      <StudyUploadWidget />
      <StudyDownloadWidget />
      <Viewer />
      <Version />
    </AppContainer>
  );
};
